import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { ReadinessScorecardSchema, type ReadinessScorecard } from './readiness';
import { z } from 'zod';

/**
 * Readiness scores write surface (T-0084 / T-0085 / E08-F02 + F03).
 *
 * The scorecard READ path lives in `readiness.ts` (T-0086 / T-0087); this
 * module focuses on the mutation surfaces used by the T-0106 scorecard
 * screen and downstream override modal hand-off (T-0113).
 *
 * In v1 the scorecard screen renders read-only — overrides are routed
 * through `data-open-override` modal triggers per T-0113. The hooks below
 * exist so the screen can pre-warm score data and invalidate the
 * scorecard cache after a successful override lands.
 */

export const ListScoreItemSchema = z
  .object({
    criterion_id: z.string(),
    criterion_name: z.string(),
    score_type: z.enum(['numeric_0_10', 'pass_fail', 'ryg']),
    score_id: z.string().optional(),
    score: z.union([z.number(), z.string()]).optional(),
    origin: z.string().optional(),
    original_ai_confidence: z.number().nullable().optional(),
    committed_confidence: z.number().nullable().optional(),
    vendor_claim: z.string().optional(),
    avero_verification: z.string().optional(),
    rollup_color: z.string().optional(),
    recommendation: z.string().nullable().optional(),
    evidence_counts: z
      .object({
        document_span: z.number(),
        cross_module_ref: z.number(),
      })
      .optional(),
    updated_at: z.string().optional(),
    ai_candidate: z
      .object({
        proposal_id: z.string(),
        score: z.union([z.number(), z.string()]),
        confidence: z.number(),
        recommendation: z.string().nullable(),
      })
      .optional(),
  })
  .passthrough();
export type ListScoreItem = z.infer<typeof ListScoreItemSchema>;

export const ListScoresResultSchema = z.object({
  items: z.array(ListScoreItemSchema),
});
export type ListScoresResult = z.infer<typeof ListScoresResultSchema>;

export function listReadinessScores(engagementId: string): Promise<ListScoresResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/scores`,
    ListScoresResultSchema,
  );
}

/**
 * Per-criterion scores list — one row per criterion, with either a committed
 * score block or an AI candidate block populated. Used by the scorecard
 * screen to render the per-criterion table with confidence + provenance.
 */
export function useReadinessScoresQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['readiness', 'scores', engagementId] as const,
    queryFn: () => listReadinessScores(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

// Re-export the scorecard schema for callers that want the scorecard-screen
// surface from a single import.
export { ReadinessScorecardSchema, type ReadinessScorecard };

/**
 * Invalidator helper — call after a score override lands (T-0113) so the
 * scorecard re-renders with the new committed value. The override modal
 * owns the actual mutation; this hook is exported so the screen can wire
 * the success callback.
 */
export function useInvalidateReadinessCaches() {
  const qc = useQueryClient();
  return (engagementId: string) => {
    void qc.invalidateQueries({ queryKey: ['readiness', 'scorecard', engagementId] });
    void qc.invalidateQueries({ queryKey: ['readiness', 'scores', engagementId] });
  };
}

/**
 * Direct override mutation hook — used by the bulk-override path the
 * scorecard screen exposes. Not used inline today (override flow goes
 * through T-0113 modal); kept here so the screen can wire it without
 * pulling in a separate module.
 */
export interface OverrideScoreInput {
  score?: unknown;
  recommendation?: string | null;
  vendor_claim?: string | null;
  avero_verification?: string | null;
  expected_date?: string | null;
  committed_confidence?: number | null;
  /**
   * T-0238: the criterion judge's pending proposal this change answers. With
   * no `score` the proposal's score is taken and, when it differs from the
   * saved one, the API records the override itself (the AI's reasoning as
   * the rationale), so `override` may be omitted.
   */
  ai_proposal_id?: string | null;
  /** With ai_proposal_id: the suggested evidence links the reviewer kept. */
  kept_links?: KeptLink[];
  override?: {
    tag: string;
    rationale: string;
  };
}

/** A suggested evidence link the reviewer kept (T-0238). */
export interface KeptLink {
  source_entity_kind: string;
  source_entity_id: string;
}

// PATCH answers { id, score, origin, override_record_id }.
export const OverrideScoreResultSchema = z
  .object({
    id: z.string(),
    score: z.union([z.number(), z.string()]).optional(),
    override_record_id: z.string().nullable(),
  })
  .passthrough();
export type OverrideScoreResult = z.infer<typeof OverrideScoreResultSchema>;

export function overrideReadinessScore(
  engagementId: string,
  scoreId: string,
  input: OverrideScoreInput,
  idempotencyKey?: string,
): Promise<OverrideScoreResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/scores/${encodeURIComponent(
      scoreId,
    )}`,
    OverrideScoreResultSchema,
    {
      method: 'PATCH',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useOverrideReadinessScoreMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scoreId,
      input,
      idempotencyKey,
    }: {
      scoreId: string;
      input: OverrideScoreInput;
      idempotencyKey?: string;
    }) => overrideReadinessScore(engagementId!, scoreId, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['readiness', 'scorecard', engagementId] });
        void qc.invalidateQueries({ queryKey: ['readiness', 'scores', engagementId] });
      }
    },
  });
}

// ===========================================================================
// Score history (T-0085 / E08-F03 — criterion history modal)
// ===========================================================================

/**
 * Score-history items mirror
 * `apps/api/src/services/readiness-scores.ts → ScoreHistoryItem`. The
 * backend interleaves audit_log entries with override_records by
 * created_at; the criterion-history modal renders the merged stream as a
 * vertical timeline.
 *
 * Two discriminated kinds:
 *   - audit     — write events from the audit trail (event_type carries
 *                 the operation name; before / after are JSON blobs).
 *   - override  — typed-tag side; reason_tag + rationale carried verbatim
 *                 from the override row.
 */
export const ScoreHistoryAuditItemSchema = z.object({
  kind: z.literal('audit'),
  audit_id: z.string().uuid(),
  event_type: z.string(),
  actor_id: z.string().nullable(),
  created_at: z.string(),
  payload: z.object({
    before: z.unknown(),
    after: z.unknown(),
  }),
});
export type ScoreHistoryAuditItem = z.infer<typeof ScoreHistoryAuditItemSchema>;

export const ScoreHistoryOverrideItemSchema = z.object({
  kind: z.literal('override'),
  override_record_id: z.string().uuid(),
  reason_tag: z.string(),
  rationale: z.string(),
  actor_id: z.string(),
  created_at: z.string(),
  previous_value: z.unknown(),
  new_value: z.unknown(),
});
export type ScoreHistoryOverrideItem = z.infer<typeof ScoreHistoryOverrideItemSchema>;

export const ScoreHistoryItemSchema = z.discriminatedUnion('kind', [
  ScoreHistoryAuditItemSchema,
  ScoreHistoryOverrideItemSchema,
]);
export type ScoreHistoryItem = z.infer<typeof ScoreHistoryItemSchema>;

export const GetScoreHistoryResultSchema = z.object({
  score_id: z.string().uuid(),
  history: z.array(ScoreHistoryItemSchema),
});
export type GetScoreHistoryResult = z.infer<typeof GetScoreHistoryResultSchema>;

export function getScoreHistory(
  engagementId: string,
  scoreId: string,
): Promise<GetScoreHistoryResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/scores/${encodeURIComponent(
      scoreId,
    )}/history`,
    GetScoreHistoryResultSchema,
  );
}

export function useScoreHistoryQuery(
  engagementId: string | undefined,
  scoreId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  const enabled = opts.enabled !== false && Boolean(engagementId) && Boolean(scoreId);
  return useQuery({
    queryKey: ['readiness', 'score-history', engagementId, scoreId] as const,
    queryFn: () => getScoreHistory(engagementId!, scoreId!),
    enabled,
    staleTime: 15_000,
  });
}

// ===========================================================================
// Bulk override (Tier 2 — POST /readiness/scores/bulk-override). Score
// values can't change in bulk (API-rejected); the bulk surface is for the
// verification axes / confidence / recommendation with one shared
// override tag + rationale.
// ===========================================================================

export interface BulkOverrideUpdates {
  vendor_claim?: string;
  avero_verification?: string;
  committed_confidence?: number;
  recommendation?: string;
}

const BulkOverrideResultSchema = z.object({ updated_count: z.number().optional() }).passthrough();

export function useBulkOverrideReadinessScoresMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scoreIds,
      updates,
      override,
      idempotencyKey,
    }: {
      scoreIds: string[];
      updates: BulkOverrideUpdates;
      override: { tag: string; rationale: string };
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/readiness/scores/bulk-override`,
        BulkOverrideResultSchema,
        {
          method: 'POST',
          body: { score_ids: scoreIds, updates, override },
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['readiness', 'scores', engagementId] });
      void qc.invalidateQueries({ queryKey: ['readiness', 'scorecard', engagementId] });
    },
  });
}

// ===========================================================================
// Score create (verification pass — POST /readiness/scores previously had
// no UI caller, leaving the entire scorecard unfillable).
// ===========================================================================

const CreateScoreResultSchema = z.object({ id: z.string() }).passthrough();

export interface CreateReadinessScoreBody {
  criterion_id: string;
  score: string | number;
  origin:
    | 'manually_authored'
    | 'ai_extracted_confirmed_as_is'
    | 'ai_extracted_edited_by_consultant';
  recommendation?: string | null;
  committed_confidence?: number | null;
  expected_date?: string | null;
  ai_proposal_id?: string | null;
  /** T-0238: with ai_proposal_id, the suggested evidence links the reviewer kept. */
  kept_links?: KeptLink[];
}

export function useCreateReadinessScoreMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateReadinessScoreBody;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/readiness/scores`,
        CreateScoreResultSchema,
        { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['readiness', 'scores', engagementId] });
      void qc.invalidateQueries({ queryKey: ['readiness', 'scorecard', engagementId] });
    },
  });
}
